import { takeWindowData } from './takeWindowData';

// The Guide's content (one guide per role). SOURCE: the design package's guide-data.js
// (Sep 29, "Aithora Console V3 package/source") and the role tutorials it reuses for
// "First 10 minutes" (care, family, resident, member and home tutorial data, Sep 18: the
// newest copies there are; the V3 package references them but does not ship them).
// Each file is a script that sets a window global, copied unchanged into ./guide/.
// Swapping in a new copy of any of them is its one import line.
import './guide/guide-data.js';
import './guide/care-tutorial-data.js';
import './guide/family-tutorial-data.js';
import './guide/resident-tutorial-data.js';
import './guide/member-tutorial-data.js';
import './guide/home-tutorial-data.js';

/** A console deep link in the design's form: {view, <stateKey>: value}. */
export type GuideGo = Record<string, string>;
/** [question, answer, deep link or null] */
export type GuideHowTo = [string, string, GuideGo | null];
/** [title, body, deep link] (Facility Admin setup, which has no tutorial) */
export type GuideFirst = [string, string, GuideGo | null];

export interface GuideRole {
  big: boolean;
  /** The tutorial (by its global's name) the First 10 minutes are taken from. */
  tutorial: string | null;
  manual?: string;
  lead: string;
  first?: GuideFirst[];
  howto: GuideHowTo[];
}

export interface GuideData {
  console: string;
  roles: Record<string, GuideRole>;
  /** Super Admin and Dev read the Operator's guide. */
  alias: Record<string, string>;
}

type Pairs = [string, string][];
export interface TutorialScreen {
  id?: string;
  n?: string;
  title: string;
  lead?: string;
  body?: string;
  promise?: Pairs;
  steps?: Pairs;
  login?: Pairs;
  badges?: Pairs;
  rows?: Pairs;
  /** [who, what, when, tone] */
  list?: [string, string, string, string?][];
  compare?: Pairs;
  points?: string[];
  tabs?: string[];
  note?: string;
  why?: string;
}
export interface Tutorial {
  role?: string;
  lead?: string;
  time?: string;
  screens: TutorialScreen[];
  end?: { title: string; lines?: string[]; ask?: string; cta?: string };
}

/**
 * The facts the Operator and Sales guides may repeat (the design's aithora-facts.js).
 * NOT IN THE DESIGN PACKAGE: the guide loads it, but no copy was shipped, so the block
 * shows its heading and no rows, exactly as the design renders without the file. Put the
 * file beside the others and import it here (or serve it from the API, as the handoff
 * asks) and the rows appear.
 */
export interface GuideFacts {
  facilitiesScored?: string;
  facilitiesOpening?: string;
  startMarkets?: string[];
  firstPilots?: string;
  founding?: { cohort: string | number; pilotDays: string | number; code: string; minDaysForResults: string | number };
  team?: { name: string; title: string }[];
  layers?: { l3?: string };
}

export const GUIDE: GuideData | null = takeWindowData<GuideData | null>('AITHORA_GUIDE', null);
export const GUIDE_FACTS: GuideFacts = takeWindowData<GuideFacts>('AITHORA_FACTS', {});

const TUTORIAL_KEYS = ['CARE_TUTORIAL', 'FAMILY_TUTORIAL', 'RESIDENT_TUTORIAL', 'MEMBER_TUTORIAL', 'HOME_TUTORIAL'] as const;
export const TUTORIALS: Record<string, Tutorial> = {};
TUTORIAL_KEYS.forEach((k) => {
  const t = takeWindowData<Tutorial | null>(k, null);
  if (t) TUTORIALS[k] = t;
});

// The "New" line and the search hint per guide, as the design's Guide page types them
// (NEWS, HINT). The handoff moves NEWS to a release-notes table when one exists.
export const GUIDE_NEWS: Record<string, string> = {
  'Care Staff': 'Every Worklist row now has Snooze and Hand off. Atlas Scribe writes your notes while you talk.',
  'Facility Admin': 'Your setup checklist is on Overview. Policy changes now go to Aithora for approval before they apply.',
  Operator: 'Oversight replaces Monitoring. System Pulse shows every system, then every facility.',
  Sales: 'CRM opens first. BCC deals@ files an email to its deal. Outcomes shows only real results.',
  Family: 'You can message the care team from the Family Portal. Visits and falls show up there too.',
};
export const GUIDE_HINT: Record<string, string> = {
  'Care Staff': 'Search: snooze, hand off, Scribe, handoff…',
  'Facility Admin': 'Search: BAA, invite, policy, test alert…',
  Operator: 'Search: approve, export, go-live, Pulse…',
  Sales: 'Search: BCC, pilot, objection, territory…',
};
